/**
 * Missed Orders (kanban fc6bca28; the first slice of the page approved as a
 * mock on review 078b211f, card e696fb23): Merlin's per-order verdicts beside
 * PJ's grade, with the one-click "That is wrong, it was X because Y" the
 * 10/27 Alpha meeting asked for, so Jake and Kevin's corrections become the
 * lessons Merlin reads on the next similar miss.
 *
 * Route: /portal/missed-orders
 *
 * Reads GET /api/portal/missed-orders/findings (the same rows PJ pulls, plus
 * every correction on each). Writes only correction rows; the finding is
 * never edited. PJ's category is the source of truth: a challenge to it is
 * shown as "awaiting PJ" until PJ answers (admins enter PJ's answer here).
 * Each correction shows where its lesson stands in Merlin's playbook bank
 * (the standard Knowledge Bank loop); an admin can retry a failed write.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import MissedOrderCorrectionControl from '../../components/portal/MissedOrderCorrectionControl';
import {
  portalMissedOrdersService,
  type CorrectionOptions,
  type FindingWithCorrections,
  type MissedOrderCorrection,
} from '../../services/portalMissedOrders';
import { trackEvent } from '../../services/portalTracking';
import { useGuruWidgetViewContext } from '../../hooks/useGuruWidgetViewContext';
import { formatDate } from '../../utils/dateFormatters';
import { triggerBlobDownload } from '../../utils/triggerDownload';
import { FIELD_LABEL, KB_STATUS_LABEL, STATUS_LABEL, rowsToCsv, valueLabel } from './missedOrdersCsv';
import styles from './PortalMissedOrdersPage.module.css';

type Window = '7' | '30' | '90' | 'all';
type Filter = 'all' | 'corrected' | 'awaiting_pj' | 'uncorrected';

const LIVE: ReadonlySet<string> = new Set(['recorded', 'challenge_open', 'challenge_upheld']);

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function PortalMissedOrdersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [rows, setRows] = useState<FindingWithCorrections[]>([]);
  const [options, setOptions] = useState<CorrectionOptions | null>(null);
  const [counts, setCounts] = useState({ total: 0, corrected: 0, challenges_open: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState<Window>('30');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState<Record<string, string>>({});

  useEffect(() => {
    trackEvent('view', 'missed_orders');
  }, []);
  // Widget metadata: the order whose detail is open (ids only, no free text).
  useGuruWidgetViewContext('missed_orders', openRow ? { order_po_number: openRow } : null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [opts, data] = await Promise.all([
        options ? Promise.resolve(options) : portalMissedOrdersService.getOptions(),
        portalMissedOrdersService.listFindings(
          windowDays === 'all' ? {} : { run_date_from: isoDaysAgo(Number(windowDays)) },
        ),
      ]);
      setOptions(opts);
      setRows(data.rows);
      setCounts({ total: data.total, corrected: data.corrected, challenges_open: data.challenges_open });
    } catch (err) {
      console.error(err);
      setError('Failed to load the missed-order findings');
    } finally {
      setLoading(false);
    }
    // options is read once; a change of window reloads the rows only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const live = r.corrections.filter((c) => LIVE.has(c.status));
      if (filter === 'corrected' && live.length === 0) return false;
      if (filter === 'uncorrected' && live.length > 0) return false;
      if (filter === 'awaiting_pj' && !r.corrections.some((c) => c.status === 'challenge_open')) return false;
      if (!q) return true;
      return [r.finding.sales_order_no, r.finding.customer_no, r.finding.customer_name, r.finding.cause_category, r.finding.merlin_subcategory, r.finding.pj_grade, r.finding.explanation]
        .some((f) => f?.toLowerCase().includes(q));
    });
  }, [rows, filter, search]);

  const patchCorrection = (updated: MissedOrderCorrection) => {
    setRows((prev) =>
      prev.map((r) =>
        r.finding.finding_id !== updated.finding_id
          ? r
          : {
              ...r,
              corrections: r.corrections.some((c) => c.id === updated.id)
                ? r.corrections.map((c) => (c.id === updated.id ? updated : c))
                : [updated, ...r.corrections],
            },
      ),
    );
  };

  const onRecorded = (c: MissedOrderCorrection) => {
    patchCorrection(c);
    setCounts((k) => ({
      ...k,
      corrected: k.corrected + (rows.find((r) => r.finding.finding_id === c.finding_id)?.corrections.some((x) => LIVE.has(x.status)) ? 0 : 1),
      challenges_open: k.challenges_open + (c.status === 'challenge_open' ? 1 : 0),
    }));
  };

  const withdraw = async (c: MissedOrderCorrection) => {
    setBusyId(c.id);
    try {
      const updated = await portalMissedOrdersService.withdraw(c.id);
      patchCorrection(updated);
      trackEvent('action', 'missed_order_correction_withdraw', {
        correction_id: c.id,
        sales_order_no: c.sales_order_no,
        field: c.field,
        outcome: 'request',
        recorded_by_dom: true,
      });
    } catch (err) {
      console.error(err);
      setError('The correction could not be withdrawn.');
    } finally {
      setBusyId(null);
    }
  };

  const resolve = async (c: MissedOrderCorrection, outcome: 'upheld' | 'declined') => {
    setBusyId(c.id);
    try {
      const updated = await portalMissedOrdersService.resolve(c.id, outcome, resolveNote[c.id]);
      patchCorrection(updated);
      setCounts((k) => ({ ...k, challenges_open: Math.max(0, k.challenges_open - 1) }));
      trackEvent('action', 'missed_order_challenge_resolve', {
        correction_id: c.id,
        sales_order_no: c.sales_order_no,
        state: outcome,
        outcome: 'request',
        recorded_by_dom: true,
      });
    } catch (err) {
      console.error(err);
      setError('The challenge could not be resolved.');
    } finally {
      setBusyId(null);
    }
  };

  const kbSync = async (c: MissedOrderCorrection) => {
    setBusyId(c.id);
    try {
      const updated = await portalMissedOrdersService.kbSync(c.id);
      patchCorrection(updated);
      trackEvent('action', 'missed_order_correction_kb_sync', {
        correction_id: c.id,
        sales_order_no: c.sales_order_no,
        state: updated.kb_write_status,
        outcome: 'request',
        recorded_by_dom: true,
      });
    } catch (err) {
      console.error(err);
      setError('The playbook write could not be retried.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleRow = (so: string) => {
    const next = openRow === so ? null : so;
    setOpenRow(next);
    trackEvent('action', 'missed_order_row_open', {
      sales_order_no: so,
      outcome: next ? 'open' : 'close',
      recorded_by_dom: true,
    });
  };

  const downloadCsv = () => {
    const csv = rowsToCsv(visible);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    triggerBlobDownload(url, `merlin_missed_orders_${windowDays === 'all' ? 'all' : `last_${windowDays}_days`}.csv`);
    URL.revokeObjectURL(url);
    trackEvent('action', 'missed_order_csv_download', {
      count: visible.length,
      filter,
      view: windowDays,
      outcome: 'none',
      recorded_by_dom: true,
    });
  };

  if (loading && rows.length === 0 && !error) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Loading Merlin's missed-order findings...</span>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="missed-orders-page">
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Missed Orders</h1>
            <p className={styles.subtitle}>
              Merlin's read on each missed order beside PJ's grade. If Merlin got it wrong, say so:
              <strong> that is wrong, it was X because Y</strong>. Every correction is recorded in
              your name and becomes a lesson Merlin reads the next time a similar order misses.
              PJ's category is the source of truth, so a correction of it goes to PJ as a
              challenge.
            </p>
          </div>
          <button
            type="button"
            className={styles.secondary}
            onClick={downloadCsv}
            disabled={visible.length === 0}
            data-action="missed_order_csv_download"
          >
            Download CSV
          </button>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{counts.total}</span>
            <span className={styles.statLabel}>Findings</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{counts.corrected}</span>
            <span className={styles.statLabel}>Corrected by a person</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{counts.challenges_open}</span>
            <span className={styles.statLabel}>Challenges awaiting PJ</span>
          </div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.chips} role="tablist" aria-label="Window">
          {(['7', '30', '90', 'all'] as Window[]).map((w) => (
            <button
              key={w}
              type="button"
              role="tab"
              aria-selected={windowDays === w}
              className={windowDays === w ? styles.chipActive : styles.chip}
              onClick={() => {
                setWindowDays(w);
                trackEvent('filter', 'missed_order_window', { view: w, outcome: 'request', recorded_by_dom: true });
              }}
              data-action="missed_order_window"
              data-value-view={w}
            >
              {w === 'all' ? 'All time' : `Last ${w} days`}
            </button>
          ))}
        </div>
        <div className={styles.chips} role="tablist" aria-label="Filter">
          {(
            [
              ['all', 'All'],
              ['uncorrected', 'Not yet corrected'],
              ['corrected', 'Corrected'],
              ['awaiting_pj', 'Awaiting PJ'],
            ] as [Filter, string][]
          ).map(([f, label]) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              className={filter === f ? styles.chipActive : styles.chip}
              onClick={() => {
                setFilter(f);
                trackEvent('filter', 'missed_order_filter', { filter: f, outcome: 'none', recorded_by_dom: true });
              }}
              data-action="missed_order_filter"
              data-value-filter={f}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          className={styles.searchInput}
          placeholder="Search order, customer, cause..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-action="missed_order_search"
          aria-label="Search"
        />
      </div>

      {error && (
        <div className={styles.errorBar} role="alert">
          <span>{error}</span>
          <button type="button" className={styles.errorRetry} onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Day</th>
              <th>PJ's grade</th>
              <th>Merlin's cause</th>
              <th>Subcategory</th>
              <th>Responsible</th>
              <th>Miss?</th>
              <th>Confidence</th>
              <th>Corrections</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={11} className={styles.emptyRow}>
                  {rows.length === 0
                    ? 'No Merlin findings in this window yet. They appear here as the missed-order investigator runs.'
                    : 'Nothing matches this filter.'}
                </td>
              </tr>
            )}
            {visible.map((r) => {
              const f = r.finding;
              const live = r.corrections.filter((c) => LIVE.has(c.status));
              const open = openRow === f.sales_order_no;
              return (
                <RowGroup
                  key={f.finding_id}
                  row={r}
                  live={live}
                  open={open}
                  isAdmin={isAdmin}
                  userId={user?.id ?? null}
                  options={options}
                  busyId={busyId}
                  resolveNote={resolveNote[f.finding_id] ?? ''}
                  onToggle={() => toggleRow(f.sales_order_no)}
                  onRecorded={onRecorded}
                  onWithdraw={withdraw}
                  onResolve={resolve}
                  onKbSync={kbSync}
                  onResolveNote={(id, note) => setResolveNote((m) => ({ ...m, [id]: note }))}
                  resolveNotes={resolveNote}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface RowGroupProps {
  row: FindingWithCorrections;
  live: MissedOrderCorrection[];
  open: boolean;
  isAdmin: boolean;
  userId: string | null;
  options: CorrectionOptions | null;
  busyId: string | null;
  resolveNote: string;
  resolveNotes: Record<string, string>;
  onToggle: () => void;
  onRecorded: (c: MissedOrderCorrection) => void;
  onWithdraw: (c: MissedOrderCorrection) => void;
  onResolve: (c: MissedOrderCorrection, outcome: 'upheld' | 'declined') => void;
  onResolveNote: (id: string, note: string) => void;
  onKbSync: (c: MissedOrderCorrection) => void;
}

function RowGroup({ row, live, open, isAdmin, userId, options, busyId, resolveNotes, onToggle, onRecorded, onWithdraw, onResolve, onResolveNote, onKbSync }: RowGroupProps) {
  const f = row.finding;
  return (
    <>
      <tr className={open ? styles.rowOpen : styles.row} data-testid="missed-order-row">
        <td>
          <button
            type="button"
            className={styles.linkButton}
            onClick={onToggle}
            data-action="missed_order_row_open"
            data-target-sales-order-no={f.sales_order_no}
            aria-expanded={open}
          >
            <span className={styles.codeBadge}>{f.sales_order_no}</span>
          </button>
        </td>
        <td>
          {f.customer_name || '—'}
          {f.customer_no && <span className={styles.custName}>{f.customer_no}</span>}
        </td>
        <td className={styles.nowrap}>{formatDate(f.run_date)}</td>
        <td>{f.pj_grade || f.pj_reason || <span className={styles.muted}>not graded</span>}</td>
        <td>{f.cause_category}</td>
        <td>{f.merlin_subcategory || <span className={styles.muted}>—</span>}</td>
        <td>{f.responsible_party || <span className={styles.muted}>—</span>}</td>
        <td>{f.is_miss == null ? '?' : f.is_miss ? 'yes' : 'no'}</td>
        <td>{f.confidence}</td>
        <td>
          {live.length === 0 ? (
            <span className={styles.muted}>none</span>
          ) : (
            <div className={styles.chipsCol}>
              {live.map((c) => (
                <span key={c.id} className={c.status === 'challenge_open' ? styles.corrChipPj : styles.corrChip} data-testid="correction-chip">
                  {FIELD_LABEL[c.field] ?? c.field}: {valueLabel(c, 'human')}
                  {c.status === 'challenge_open' ? ' · awaiting PJ' : c.status === 'challenge_upheld' ? ' · PJ upheld' : ''}
                </span>
              ))}
            </div>
          )}
        </td>
        <td>
          {options && <MissedOrderCorrectionControl finding={f} options={options} onRecorded={onRecorded} />}
        </td>
      </tr>
      {open && (
        <tr className={styles.detailRow} data-testid="missed-order-detail">
          <td colSpan={11}>
            <div className={styles.detail}>
              <div className={styles.detailBlock}>
                <div className={styles.detailLabel}>Why it mattered</div>
                <div>{f.why_flagged || '—'}</div>
                <div className={styles.detailLabel}>Merlin's explanation</div>
                <div>{f.explanation}</div>
                {f.evidence_refs && (
                  <>
                    <div className={styles.detailLabel}>Evidence</div>
                    <pre className={styles.pre}>{f.evidence_refs}</pre>
                  </>
                )}
                {f.open_question && (
                  <>
                    <div className={styles.detailLabel}>Open question</div>
                    <div>{f.open_question}</div>
                  </>
                )}
              </div>
              <div className={styles.detailBlock}>
                <div className={styles.detailLabel}>Corrections on this order ({row.corrections.length})</div>
                {row.corrections.length === 0 && <div className={styles.muted}>Nobody has corrected this finding.</div>}
                {row.corrections.map((c) => (
                  <div key={c.id} className={c.status === 'withdrawn' ? styles.corrCardMuted : styles.corrCard} data-testid="correction-card">
                    <div className={styles.corrHead}>
                      <strong>{FIELD_LABEL[c.field] ?? c.field}</strong>: {valueLabel(c, 'merlin')} → <strong>{valueLabel(c, 'human')}</strong>
                      <span className={styles.statusPill}>{STATUS_LABEL[c.status] ?? c.status}</span>
                    </div>
                    <div className={styles.corrWho}>
                      {c.corrected_by_name || c.corrected_by_email || 'someone'} · {formatDate(c.corrected_at)} ·{' '}
                      <span
                        className={c.kb_write_status === 'failed' ? styles.kbFailed : styles.kbOk}
                        title={c.kb_write_error ?? (c.kb_entry_id ? `entry ${c.kb_entry_id}` : undefined)}
                        data-testid="kb-status"
                      >
                        {KB_STATUS_LABEL[c.kb_write_status] ?? c.kb_write_status}
                      </span>
                    </div>
                    <div className={styles.corrReason}>{c.reason}</div>
                    {c.resolution_note && <div className={styles.corrReason}>PJ: {c.resolution_note}</div>}
                    <div className={styles.corrActions}>
                      {(c.status === 'recorded' || c.status === 'challenge_open') && (isAdmin || c.corrected_by_user_id === userId) && (
                        <button
                          type="button"
                          className={styles.tinyButton}
                          disabled={busyId === c.id}
                          onClick={() => onWithdraw(c)}
                          data-action="missed_order_correction_withdraw"
                          data-target-correction-id={c.id}
                        >
                          Withdraw
                        </button>
                      )}
                      {isAdmin && c.kb_write_status === 'failed' && (
                        <button
                          type="button"
                          className={styles.tinyButton}
                          disabled={busyId === c.id}
                          onClick={() => onKbSync(c)}
                          data-action="missed_order_correction_kb_sync"
                          data-target-correction-id={c.id}
                        >
                          Retry playbook write
                        </button>
                      )}
                      {isAdmin && c.status === 'challenge_open' && (
                        <>
                          <input
                            className={styles.tinyInput}
                            placeholder="PJ's note (optional)"
                            value={resolveNotes[c.id] ?? ''}
                            onChange={(e) => onResolveNote(c.id, e.target.value)}
                            aria-label="PJ's note"
                          />
                          <button type="button" className={styles.tinyButton} disabled={busyId === c.id} onClick={() => onResolve(c, 'upheld')} data-action="missed_order_challenge_resolve" data-target-correction-id={c.id} data-value-state="upheld">
                            PJ upheld
                          </button>
                          <button type="button" className={styles.tinyButton} disabled={busyId === c.id} onClick={() => onResolve(c, 'declined')} data-action="missed_order_challenge_resolve" data-target-correction-id={c.id} data-value-state="declined">
                            PJ declined
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
